import type {
  DailyMetrics,
  FulfillmentMode,
  InternalProductInfoRecord,
  InventoryCostOperation,
  InventoryCostMode,
  ProductMaster,
  ProductMasterDefaults,
  ProductMasterRecord,
  ProductRecord,
  ShippingProvider,
} from './types'
import { rowToMetrics, sumMetrics } from './utils'

export interface EstimatedProfit {
  metrics: DailyMetrics
  profit: number | null
  margin: number | null
  fulfillmentCost: number | null
  rateFee: number | null
  coveredDates: number
  activeDates: number
}

export interface ResolvedProductCost {
  packagingCost: number
  giftCost: number
  shippingInsuranceFee: number
  shippingProvider: ShippingProvider
  shippingFee: number
  jdShippingFee: number
  ytoShippingFee: number
  platformFeeRate: number
  taxRate: number
}

export interface InternalProductCostResolution {
  generalAgentPrice: number | null
  brandSpecialPrice: number | null
  productCost: number | null
  isBrandSpecial: boolean
  inventoryCostMode: InventoryCostMode
  issue: 'general-price-conflict' | 'special-price-conflict' | null
}

export const DEFAULT_PRODUCT_MASTER_COSTS: ProductMasterDefaults = {
  packagingCost: 2,
  giftCost: 3,
  shippingInsuranceFee: 1.5,
  shippingProvider: 'jd-express',
  jdWarehouseFee: 11,
  jdShippingFee: 9,
  ytoShippingFee: 5,
  platformFeeRate: 8.6,
  taxRate: 6,
}

export const JD_STORE_ID = 'jd-588485' as const

const requireProductMasterStore = (productMaster: ProductMaster | null, storeId: string) => {
  if (storeId !== JD_STORE_ID || (productMaster && productMaster.storeId !== storeId)) {
    throw new Error(`商品成本作用域不匹配：${storeId}`)
  }
}

export const productMasterRecordMap = (productMaster: ProductMaster | null, storeId: string) => {
  requireProductMasterStore(productMaster, storeId)
  return new Map((productMaster?.records || []).map((record) => [record.skuId, record]))
}

export const productMasterCostDefaults = (productMaster: ProductMaster | null, storeId: string): ProductMasterDefaults => {
  requireProductMasterStore(productMaster, storeId)
  return {
    ...DEFAULT_PRODUCT_MASTER_COSTS,
    ...(productMaster?.defaults || {}),
    ...({ storeCostProfile: productMaster?.storeCostProfile } as object),
  }
}

type ScopedProductMasterDefaults = ProductMasterDefaults & {
  storeCostProfile?: ProductMaster['storeCostProfile']
}

const storeVersionOn = (
  versions: NonNullable<ProductMaster['storeCostProfile']>['defaultVersions'] | undefined,
  date: string,
) => [...(versions || [])]
  .filter((version) => version.calculationReady && version.effectiveDate <= date)
  .sort((left, right) => right.effectiveDate.localeCompare(left.effectiveDate))[0]

const defaultsOn = (defaults: ProductMasterDefaults, date: string) => {
  const profile = (defaults as ScopedProductMasterDefaults).storeCostProfile
  const actual = storeVersionOn(profile?.actualSettlementVersions, date)
  const storeDefault = storeVersionOn(profile?.defaultVersions, date)
  const platformTemplate = storeVersionOn(profile?.platformTemplateVersions, date)
  const version = actual || storeDefault || platformTemplate
  if (!version) return { defaults, actual: undefined }
  const shippingProvider = version.shippingProviderCode as ShippingProvider
  const resolved: ProductMasterDefaults = {
    ...defaults,
    packagingCost: valueOrDefault(version.packagingCost, defaults.packagingCost),
    giftCost: valueOrDefault(version.giftCost, defaults.giftCost),
    shippingInsuranceFee: valueOrDefault(version.shippingInsuranceFee, defaults.shippingInsuranceFee),
    shippingProvider,
    platformFeeRate: valueOrDefault(version.platformFeeRate, defaults.platformFeeRate),
    taxRate: valueOrDefault(version.taxRate, defaults.taxRate),
  }
  if (version.shippingFee !== null) {
    if (shippingProvider === 'jd-warehouse') resolved.jdWarehouseFee = version.shippingFee
    if (shippingProvider === 'jd-express') resolved.jdShippingFee = version.shippingFee
    if (shippingProvider === 'yto-express') resolved.ytoShippingFee = version.shippingFee
  }
  return { defaults: resolved, actual }
}

export const costVersionOn = (record: ProductMasterRecord | undefined, date: string) =>
  [...(record?.costVersions || [])]
    .filter((version) => version.effectiveDate <= date)
    .sort((left, right) => right.effectiveDate.localeCompare(left.effectiveDate))[0]

export const effectiveFulfillmentMode = (
  record: ProductMasterRecord | undefined,
  inheritedJdWarehouse: boolean,
): FulfillmentMode => record?.fulfillmentMode || (inheritedJdWarehouse ? 'jd-warehouse' : 'merchant-direct')

const valueOrDefault = (value: number | null | undefined, fallback: number) =>
  value === null || value === undefined ? fallback : value

const uniqueInternalPrice = (
  records: InternalProductInfoRecord[],
  field: 'generalAgentPrice' | 'brandSpecialInitialPrice' | 'specialPrice0225' | 'specialPrice0305',
) => {
  const values = Array.from(new Set(
    records
      .map((record) => record[field])
      .filter((value): value is number => typeof value === 'number' && value > 0),
  ))
  return {
    value: values.length === 1 ? values[0] : null,
    conflict: values.length > 1,
  }
}

export function internalProductCost(
  records: InternalProductInfoRecord[],
): InternalProductCostResolution {
  const generalPrice = uniqueInternalPrice(records, 'generalAgentPrice')
  const specialFields: Array<
    'brandSpecialInitialPrice' | 'specialPrice0225' | 'specialPrice0305'
  > = ['specialPrice0305', 'specialPrice0225', 'brandSpecialInitialPrice']

  for (const field of specialFields) {
    const price = uniqueInternalPrice(records, field)
    if (price.conflict) {
      return {
        generalAgentPrice: generalPrice.value,
        brandSpecialPrice: null,
        productCost: null,
        isBrandSpecial: true,
        inventoryCostMode: 'brand-special',
        issue: 'special-price-conflict',
      }
    }
    if (price.value !== null) {
      return {
        generalAgentPrice: generalPrice.value,
        brandSpecialPrice: price.value,
        productCost: price.value,
        isBrandSpecial: true,
        inventoryCostMode: 'brand-special',
        issue: null,
      }
    }
  }

  return {
    generalAgentPrice: generalPrice.value,
    brandSpecialPrice: null,
    productCost: generalPrice.value,
    isBrandSpecial: false,
    inventoryCostMode: 'general-agent',
    issue: generalPrice.conflict ? 'general-price-conflict' : null,
  }
}

type InventoryCostOperationSource = { inventoryCostOperations?: InventoryCostOperation[] }

const inventoryCostOperationOn = (record: InventoryCostOperationSource | undefined, date: string) =>
  [...(record?.inventoryCostOperations || [])]
    .filter((operation) => operation.effectiveDate <= date)
    .sort((left, right) => {
      const dateOrder = right.effectiveDate.localeCompare(left.effectiveDate)
      return dateOrder || Date.parse(right.changedAt) - Date.parse(left.changedAt)
    })[0]

export const inventoryCostModeOn = (
  record: InventoryCostOperationSource | undefined,
  date: string,
  fallback: InventoryCostMode,
) => inventoryCostOperationOn(record, date)?.mode || fallback

export function internalProductCostOn(
  records: InternalProductInfoRecord[],
  record: InventoryCostOperationSource | undefined,
  date: string,
): InternalProductCostResolution {
  const base = internalProductCost(records)
  const inventoryCostMode = inventoryCostModeOn(record, date, base.inventoryCostMode)
  if (inventoryCostMode === 'brand-special') return {
    ...base,
    productCost: base.brandSpecialPrice,
    inventoryCostMode,
  }

  const generalPrice = uniqueInternalPrice(records, 'generalAgentPrice')
  return {
    ...base,
    productCost: generalPrice.value,
    inventoryCostMode,
    issue: generalPrice.conflict ? 'general-price-conflict' : null,
  }
}

const inferredAccessory = (
  product: ProductRecord,
  companyRecords: InternalProductInfoRecord[],
) => {
  const text = [
    product.name,
    product.sourceName,
    ...product.category,
    ...companyRecords.map((record) => record.productName),
  ].join(' ')
  const hasLowEcommercePrice = companyRecords.some((record) =>
    typeof record.ecommercePrice === 'number'
    && record.ecommercePrice > 0
    && record.ecommercePrice < 30,
  )
  return text.includes('配件') || hasLowEcommercePrice
}

export const accessoryFor = (
  product: ProductRecord,
  record: ProductMasterRecord | undefined,
  companyRecords: InternalProductInfoRecord[],
) => record?.isAccessory ?? inferredAccessory(product, companyRecords)

export const shippingProviderFor = (
  record: ProductMasterRecord | undefined,
  inheritedJdWarehouse: boolean,
  selectedDate: string,
  defaults: ProductMasterDefaults,
  isAccessory: boolean,
  isBrandSpecial: boolean,
) => {
  const effectiveDefaults = defaultsOn(defaults, selectedDate)
  return effectiveDefaults.actual?.shippingProviderCode as ShippingProvider
    || (isAccessory || isBrandSpecial ? 'yto-express' : costVersionOn(record, selectedDate)?.shippingProvider
  || (effectiveFulfillmentMode(record, inheritedJdWarehouse) === 'jd-warehouse' ? 'jd-warehouse' : effectiveDefaults.defaults.shippingProvider))
}

export const rebateRateFor = (
  isAccessory: boolean,
  isBrandSpecial: boolean,
  productCost: number | null,
) => productCost !== null && !isAccessory && !isBrandSpecial ? 0.2 : null

export const giftCostFor = (
  isAccessory: boolean,
  isBrandSpecial: boolean,
  defaults: ProductMasterDefaults,
) => isAccessory || isBrandSpecial ? 0 : defaults.giftCost

export const rebateAmountFor = (
  purchaseCost: number | null,
  rebateRate: number | null,
  soldItemCount: number,
) => rebateRate === null
  ? null
  : soldItemCount <= 0
    ? 0
    : purchaseCost === null
      ? null
      : purchaseCost * rebateRate * soldItemCount

export const profitWithRebateFor = (
  profit: number | null | undefined,
  rebateAmount: number | null,
) => profit === null || profit === undefined ? null : profit + (rebateAmount || 0)

export function rebateAmountForSku(
  product: ProductRecord,
  dates: string[],
  companyRecords: InternalProductInfoRecord[],
  isAccessory: boolean,
) {
  const dateSet = new Set(dates)
  const internalCost = internalProductCost(companyRecords)
  const rebateRate = rebateRateFor(
    isAccessory,
    internalCost.isBrandSpecial,
    internalCost.productCost,
  )
  if (rebateRate === null) return null
  let amount = 0
  let hasRebate = false
  for (const row of product.history) {
    if (!dateSet.has(row[0])) continue
    const daily = rowToMetrics(row)
    if (daily.soldItemCount <= 0) continue
    hasRebate = true
    if (internalCost.productCost === null) return null
    amount += internalCost.productCost * rebateRate * daily.soldItemCount
  }
  return hasRebate ? amount : null
}

export function resolvedProductCost(
  record: ProductMasterRecord | undefined,
  date: string,
  defaults: ProductMasterDefaults,
  shippingProviderOverride?: ShippingProvider,
  giftExcluded = false,
): ResolvedProductCost {
  const scoped = defaultsOn(defaults, date)
  defaults = scoped.defaults
  const version = costVersionOn(record, date)
  const actual = scoped.actual
  const shippingProvider = actual?.shippingProviderCode as ShippingProvider || shippingProviderOverride || version?.shippingProvider || defaults.shippingProvider
  const jdShippingFee = valueOrDefault(actual?.shippingProviderCode === 'jd-express' ? actual.shippingFee : version?.jdShippingFee, defaults.jdShippingFee)
  const ytoShippingFee = valueOrDefault(actual?.shippingProviderCode === 'yto-express' ? actual.shippingFee : version?.ytoShippingFee, defaults.ytoShippingFee)
  const fallbackShippingFee = shippingProvider === 'jd-warehouse'
    ? defaults.jdWarehouseFee
    : shippingProvider === 'yto-express'
      ? ytoShippingFee
      : jdShippingFee
  const storedShippingFee = actual?.shippingFee ?? (!shippingProviderOverride || version?.shippingProvider === shippingProviderOverride
    ? version?.shippingFee
    : null
  )
  return {
    packagingCost: valueOrDefault(actual?.packagingCost, valueOrDefault(version?.packagingCost, defaults.packagingCost)),
    giftCost: actual?.giftCost ?? (giftExcluded ? 0 : defaults.giftCost),
    shippingInsuranceFee: valueOrDefault(actual?.shippingInsuranceFee, defaults.shippingInsuranceFee),
    shippingProvider,
    shippingFee: valueOrDefault(storedShippingFee, fallbackShippingFee),
    jdShippingFee: shippingProvider === 'jd-express' ? jdShippingFee : 0,
    ytoShippingFee: shippingProvider === 'yto-express' ? ytoShippingFee : 0,
    platformFeeRate: valueOrDefault(actual?.platformFeeRate, valueOrDefault(version?.platformFeeRate, defaults.platformFeeRate)),
    taxRate: valueOrDefault(actual?.taxRate, valueOrDefault(version?.taxRate, defaults.taxRate)),
  }
}

export function unitOperatingCost(cost: ResolvedProductCost, purchaseCost: number) {
  return purchaseCost
    + cost.packagingCost
    + cost.shippingFee
    + cost.giftCost
    + cost.shippingInsuranceFee
}

export function estimatedProfitForSku(
  product: ProductRecord,
  record: ProductMasterRecord | undefined,
  dates: string[],
  defaults: ProductMasterDefaults,
  companyRecords: InternalProductInfoRecord[],
  shippingProviderOverride?: ShippingProvider,
): EstimatedProfit {
  const dateSet = new Set(dates)
  const rows = product.history.filter((row) => dateSet.has(row[0]))
  const metrics = sumMetrics(rows.map((row) => rowToMetrics(row)))
  const internalCost = internalProductCost(companyRecords)
  const isAccessory = accessoryFor(product, record, companyRecords)
  const giftExcluded = isAccessory || internalCost.isBrandSpecial
  let profit = 0
  let fulfillmentCost = 0
  let rateFee = 0
  let coveredDates = 0
  let activeDates = 0
  let complete = true

  for (const row of rows) {
    const daily = rowToMetrics(row)
    if (daily.gmv <= 0 && daily.soldItemCount <= 0) continue
    activeDates += 1
    const productCost = internalProductCostOn(companyRecords, record, daily.date).productCost
    if (productCost === null) {
      complete = false
      continue
    }
    const cost = resolvedProductCost(
      record,
      daily.date,
      defaults,
      shippingProviderOverride,
      giftExcluded,
    )
    coveredDates += 1
    const dailyFulfillmentCost = unitOperatingCost(cost, productCost) * daily.soldItemCount
    const dailyRateFee = daily.gmv * (cost.platformFeeRate + cost.taxRate) / 100
    fulfillmentCost += dailyFulfillmentCost
    rateFee += dailyRateFee
    profit += daily.gmv - dailyFulfillmentCost - dailyRateFee
  }

  const resolvedProfit = complete && activeDates > 0 ? profit : null
  return {
    metrics,
    profit: resolvedProfit,
    margin: resolvedProfit !== null && metrics.gmv > 0 ? resolvedProfit / metrics.gmv : null,
    fulfillmentCost: resolvedProfit === null ? null : fulfillmentCost,
    rateFee: resolvedProfit === null ? null : rateFee,
    coveredDates,
    activeDates,
  }
}
